import type { Registry as Services } from '@ember/service';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import dayjs from 'dayjs';
import type { PeriodParity } from 'lumen/models/period';
import type { HotelOrMarket } from 'lumen/services/hotel-and-market';
import { POSITION_TO_BASERATE } from 'lumen/utils/constants/general-constants';

export interface BaseRate {
  channel: IconBrandName;
  extractDatetimeFromNow: string;
  extract_datetime: string;
  rate: string | null;
  rateUnconverted: string;
  source: string;
  title: string;
}

export interface FormattedParity {
  extractDatetimeFromNow: string;
  extract_datetime: string;
  linkoutSource: string;
  loss: string | null;
  ota: string;
  pos: string;
  rate: string | null;
  rateUnconverted: string;
  source: string;
  title: string;
}

const LOSS_NON_COMPUTABLE: number[] = [
  POSITION_TO_BASERATE.NO_DATA,
  POSITION_TO_BASERATE.LOSS_SOLDOUT,
];

export function formatParity(
  otaService: Services['ota'],
  ratesService: Services['rates'],
  hotel: HotelOrMarket | null,
  parity: PeriodParity,
): FormattedParity {
  return {
    title: otaService.otaIdToPrettyName(parity.channel),
    ota: parity.ota,
    rate: ratesService.formatRate(parity, hotel),
    // used in POS view
    rateUnconverted: `${ratesService.formatRateObject(parity, false)} ${parity.currency}`,
    loss: formatLoss(parity, hotel, ratesService),
    linkoutSource: parity.ota,
    source: otaService.otaIdToPrettyName(parity.ota),
    pos: parity.pos,
    extract_datetime: parity.extract_datetime,
    extractDatetimeFromNow: dayjs.utc(parity.extract_datetime).fromNow(),
  };
}

export function formatBaseRate(
  otaService: Services['ota'],
  ratesService: Services['rates'],
  hotel: HotelOrMarket | null,
  baseRate: PeriodParity,
): BaseRate {
  const { channel, currency, extract_datetime, ota } = baseRate;

  return {
    channel: channel as IconBrandName,
    extractDatetimeFromNow: dayjs.utc(extract_datetime).fromNow(),
    extract_datetime,
    rate: ratesService.formatRate(baseRate, hotel),
    rateUnconverted: [
      ratesService.formatRateObject(baseRate, false),
      currency,
    ].join(' '),
    source: otaService.otaIdToPrettyName(ota),
    title: otaService.otaIdToPrettyName(channel),
  };
}

export function sortParities(a: PeriodParity, b: PeriodParity): number {
  let sortedRes = a.difference_with_baserate - b.difference_with_baserate;
  if (sortedRes === 0) {
    sortedRes = a.ota.localeCompare(b.ota);

    if (sortedRes === 0) {
      sortedRes = a.channel.localeCompare(b.channel);
    }
  }

  return sortedRes;
}

export function formatLoss(
  parity: PeriodParity,
  hotel: HotelOrMarket | null,
  ratesService: Services['rates'],
): string | null {
  if (LOSS_NON_COMPUTABLE.includes(parity.position_to_baserate)) {
    return '--';
  }

  return ratesService.formatRate(
    Math.round(parity.difference_with_baserate),
    hotel,
    { includeCurrencySymbol: false },
  );
}
